import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import SheinDateRangeFilter, {
  pickDatePart,
  toDayEndDateTime,
  toDayStartDateTime,
  validateDateRange,
} from '@/shein-platform/components/SheinDateRangeFilter';
import SheinSyncRangeModal from '@/shein-platform/components/SheinSyncRangeModal';
import BatchQueryModal from '@/shein-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/shein-platform/modules/products/components/batchQueryUtils';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { useSheinShop } from '@/shein-platform/context/SheinShopContext';
import {
  SALES_ORDER_STATUS_TABS,
} from '@/shein-platform/constants/sheinApiCatalog';
import { buildSheinPath } from '@/shein-platform/constants/sheinRoutes';
import PlatformShopDimensionTabs from '@/components/platform/PlatformShopDimensionTabs';
import PlatformShopPickModal from '@/components/platform/PlatformShopPickModal';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
  isConcreteShopId,
  resolveConcreteShopId,
} from '@/utils/platformShopScope';
import {
  formatTrackTime,
  listDateRows,
  listLogisticsSummary,
  listOrderInfoRows,
  listOrderProductSummary,
  listPrimaryPrices,
  salesStatusLabel,
  trackInfoList,
} from '@/shein-platform/modules/aftersales/salesOrderDisplay';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { extractSheinList, extractSheinTotal, pickField } from '@/shein-platform/utils/sheinResponse';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import { showToast } from '@/utils/toast';
import { readNoticeOrderNo } from '@/shein-platform/utils/sheinNoticeFocusQuery';
import '@/styles/shein-platform/shein-product-ops.css';

const ORDER_ID_OPTIONS = [
  { value: 'orderNo', labelKey: 'orderList.orderNo' },
  { value: 'waybillNo', labelKey: 'orderList.waybillNo' },
];

const EMPTY_FILTER = {
  orderIdType: 'orderNo',
  orderIdValue: '',
  skc: '',
  orderStatus: '',
  startTime: '',
  endTime: '',
};

function rowKey(row, index) {
  return String(pickField(row, 'orderNo', 'orderNumber') || `row-${index}`);
}

function validateOrderWindow(startDate, endDate) {
  const basic = validateDateRange(startDate, endDate);
  if (basic) return basic;
  const start = pickDatePart(startDate);
  const end = pickDatePart(endDate);
  if (!start || !end) return '';
  const ms = new Date(`${end}T00:00:00`).getTime() - new Date(`${start}T00:00:00`).getTime();
  if (ms > 7 * 24 * 60 * 60 * 1000) {
    return i18n.t('orderList.window7', { ns: 'workbench' });
  }
  return '';
}

function statusModifier(code) {
  const n = Number(code);
  if (n === 5) return 'success';
  if (n === 6 || n === 8 || n === 9) return 'failed';
  return 'pending';
}

/**
 * 订单列表：展示产品 / 订单 / 物流 / 状态 / 日期 / 价格。
 * 同步：order-list → order-detail →（有运单号才）logistics-track。
 */
export default function OrderListPage() {
  const { t } = useTranslation('workbench');
  const statusText = (code) => {
    const raw = salesStatusLabel(code);
    const key = ({
      '全部': 'tabAll',
      '待付款': 'tabUnpaid',
      '保留中': 'tabOnHold',
      '待发货': 'tabAwaitShip',
      '部分发货': 'tabPartialShip',
      '待揽收': 'tabAwaitCollect',
      '运输中': 'tabInTransit',
      '已送达': 'tabDelivered',
      '已完成': 'tabCompleted',
      '已取消': 'tabCancelled',
      '待处理': 'tabPending',
      '待TEMU发货': 'tabTemuShip',
      '待SHEIN发货': 'tabSheinShip',
      '已发货': 'tabShipped',
      '已签收': 'tabSigned',
      '用户已退款': 'tabUserRefund',
      '已报损': 'tabDamaged',
      '已拒收': 'tabRejected',
      '已退款': 'tabRefunded',
    })[raw];
    return key ? t(`orderList.${key}`) : raw;
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    activeShopId,
    scopedShops,
    expectedShopType,
    isAllShopsMode,
    loading: shopLoading,
  } = useSheinShop();
  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();
  const pendingSyncRangeRef = useRef(null);
  const [pageShopScope, setPageShopScope] = useState(() => activeShopId || '');
  const [shopPickOpen, setShopPickOpen] = useState(false);

  const [filterDraft, setFilterDraft] = useState(() => ({ ...EMPTY_FILTER }));
  const [query, setQuery] = useState(() => ({ ...EMPTY_FILTER, _nonce: 0 }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchModalField, setBatchModalField] = useState(null); // orderIdValue | skc
  const [syncRangeOpen, setSyncRangeOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [trackPanel, setTrackPanel] = useState(null);
  const [statusCounts, setStatusCounts] = useState(() => ({}));
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const selectedRowsRef = useRef(new Map());

  useEffect(() => {
    setPageShopScope(activeShopId || '');
  }, [activeShopId]);

  useEffect(() => {
    setPage(1);
    setRows([]);
    setTotal(0);
    setError('');
    setLastSyncedAt('');
    setPersistedSyncedAt('');
    setTrackPanel(null);
    setStatusCounts({});
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, [pageShopScope]);

  const listReady = Boolean(pageShopScope)
    && (isConcreteShopId(pageShopScope) || (isAllShopsScope(pageShopScope) && expectedShopType));
  const concreteScopeId = resolveConcreteShopId(pageShopScope);

  const noticeOrderNo = readNoticeOrderNo(searchParams);
  useEffect(() => {
    if (!noticeOrderNo) return;
    const next = {
      ...EMPTY_FILTER,
      orderIdType: 'orderNo',
      orderIdValue: noticeOrderNo,
    };
    setFilterDraft(next);
    setQuery((prev) => ({ ...next, _nonce: (prev._nonce || 0) + 1 }));
    setPage(1);
  }, [noticeOrderNo, activeShopId]);

  useEffect(() => {
    if (!activeShopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getShopSyncStatus(activeShopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const mod = modules.find((m) => String(m.module || '').toUpperCase() === 'ORDER');
        const at = mod?.lastSyncEndAt || mod?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [activeShopId]);

  const queryParams = useMemo(() => {
    const statusNum = query.orderStatus === '' || query.orderStatus == null
      ? null
      : Number(query.orderStatus);
    const startDate = pickDatePart(query.startTime);
    const endDate = pickDatePart(query.endTime);
    const idType = query.orderIdType === 'waybillNo' ? 'waybillNo' : 'orderNo';
    const idValue = query.orderIdValue || undefined;
    return {
      page,
      pageSize,
      source: 'cached',
      orderStatus: Number.isFinite(statusNum) ? statusNum : undefined,
      orderNo: idType === 'orderNo' ? idValue : undefined,
      waybillNo: idType === 'waybillNo' ? idValue : undefined,
      skc: query.skc || undefined,
      startTime: toDayStartDateTime(startDate) || undefined,
      endTime: toDayEndDateTime(endDate) || undefined,
      _nonce: query._nonce,
    };
  }, [query, page, pageSize]);

  const load = useCallback(async () => {
    if (!listReady) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { _nonce, ...params } = queryParams;
      let res;
      if (isAllShopsScope(pageShopScope)) {
        res = await sheinPlatformAPI.listSheinOrders(PLATFORM_ALL_SHOPS_ID, {
          ...params,
          allOfType: true,
          shopType: expectedShopType,
        });
      } else {
        res = await sheinPlatformAPI.listSheinOrders(pageShopScope, params);
      }
      const list = extractSheinList(res);
      setRows(list);
      setTotal(extractSheinTotal(res, list.length));
      const counts = res?.statusCounts && typeof res.statusCounts === 'object' ? res.statusCounts : {};
      setStatusCounts(counts);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setStatusCounts({});
      setError(e?.message || t('orderList.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [listReady, pageShopScope, expectedShopType, queryParams, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = useCallback(async () => {
    const range = pendingSyncRangeRef.current;
    const shopId = concreteScopeId;
    if (!shopId || !range?.startTime || !range?.endTime) return;
    try {
      const track = await startJob({
        kind: 'orders',
        shopId,
        label: t('orderList.syncUpdate'),
        payload: {
          startTime: range.startTime,
          endTime: range.endTime,
        },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      const finishedAt = new Date().toISOString();
      setLastSyncedAt(finishedAt);
      setPersistedSyncedAt(finishedAt);
      setQuery((prev) => ({
        ...prev,
        startTime: pickDatePart(range.startTime) || prev.startTime,
        endTime: pickDatePart(range.endTime) || prev.endTime,
        _nonce: prev._nonce + 1,
      }));
      setFilterDraft((prev) => ({
        ...prev,
        startTime: pickDatePart(range.startTime) || prev.startTime,
        endTime: pickDatePart(range.endTime) || prev.endTime,
      }));
      setPage(1);
      setSyncRangeOpen(false);
      pendingSyncRangeRef.current = null;
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      showToast(e?.message || t('orderList.syncFailed'), 'error');
    }
  }, [concreteScopeId, startJob, onTrackDone, t]);

  const {
    refresh: guardedSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncingLocal || isBusy('orders', concreteScopeId || activeShopId);

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const onQuery = () => {
    const orderTokens = parseBatchTokens(filterDraft.orderIdValue).slice(0, BATCH_QUERY_LIMIT);
    const skcTokens = parseBatchTokens(filterDraft.skc).slice(0, BATCH_QUERY_LIMIT);
    const start = pickDatePart(filterDraft.startTime);
    const end = pickDatePart(filterDraft.endTime);
    const rangeError = validateDateRange(start, end);
    if (rangeError) {
      showToast(rangeError, 'error');
      return;
    }
    const windowErr = validateOrderWindow(start, end);
    if (windowErr) {
      showToast(windowErr, 'warning');
      return;
    }
    if (activeShopId) setLoading(true);
    setPage(1);
    setTrackPanel(null);
    setQuery((prev) => ({
      orderIdType: filterDraft.orderIdType === 'waybillNo' ? 'waybillNo' : 'orderNo',
      orderIdValue: orderTokens.join(','),
      skc: skcTokens.join(','),
      orderStatus: prev.orderStatus || '',
      startTime: start,
      endTime: end,
      _nonce: prev._nonce + 1,
    }));
  };

  const onReset = () => {
    setFilterDraft((prev) => ({
      ...EMPTY_FILTER,
      orderStatus: prev.orderStatus || '',
    }));
    setPage(1);
    setTrackPanel(null);
    setQuery((prev) => ({
      ...EMPTY_FILTER,
      orderStatus: prev.orderStatus || '',
      _nonce: prev._nonce + 1,
    }));
  };

  const onStatusTab = useCallback((value) => {
    const nextStatus = value == null ? '' : String(value);
    setFilterDraft((prev) => ({ ...prev, orderStatus: nextStatus }));
    setPage(1);
    setTrackPanel(null);
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
    setQuery((prev) => ({
      ...prev,
      orderStatus: nextStatus,
      _nonce: prev._nonce + 1,
    }));
  }, []);

  const listTabs = useMemo(() => (
    <>
      {isAllShopsMode && scopedShops.length ? (
        <PlatformShopDimensionTabs
          shops={scopedShops}
          value={pageShopScope || PLATFORM_ALL_SHOPS_ID}
          onChange={setPageShopScope}
          showAll={scopedShops.length >= 2}
          ariaLabel="店铺维度"
        />
      ) : null}
      <div className="spo-status-tabs" role="tablist" aria-label={t('orderList.tabsAria')}>
        {SALES_ORDER_STATUS_TABS.map((tab) => {
          const countKey = tab.value === '' ? 'all' : String(tab.value);
          const count = Number(statusCounts?.[countKey] ?? 0);
          const active = String(query.orderStatus || '') === String(tab.value);
          return (
            <button
              key={tab.value || 'all'}
              type="button"
              role="tab"
              aria-selected={active}
              className={`spo-status-tab${active ? ' spo-status-tab--active' : ''}`}
              onClick={() => onStatusTab(tab.value)}
            >
              {t(`orderList.${({
              '全部': 'tabAll',
              '待付款': 'tabUnpaid',
              '保留中': 'tabOnHold',
              '待发货': 'tabAwaitShip',
              '部分发货': 'tabPartialShip',
              '待揽收': 'tabAwaitCollect',
              '运输中': 'tabInTransit',
              '已送达': 'tabDelivered',
              '已完成': 'tabCompleted',
              '已取消': 'tabCancelled',
              '待处理': 'tabPending',
              '待TEMU发货': 'tabTemuShip',
              '待SHEIN发货': 'tabSheinShip',
              '已发货': 'tabShipped',
              '已签收': 'tabSigned',
              '用户已退款': 'tabUserRefund',
              '已报损': 'tabDamaged',
              '已拒收': 'tabRejected',
              '已退款': 'tabRefunded',
            })[tab.label] || 'tabAll'}`, { defaultValue: tab.label })}
              <span className="spo-status-tab__count">{Number.isFinite(count) ? count : 0}</span>
            </button>
          );
        })}
      </div>
    </>
  ), [isAllShopsMode, scopedShops, pageShopScope, query.orderStatus, statusCounts, onStatusTab, t]);

  const emptyTitle = !listReady
    ? t('orderList.pickShop')
    : (error ? t('orderList.loadFailedShort') : t('orderList.sheinEmpty'));
  const emptyDesc = !listReady
    ? t('orderList.sheinEmptyDescAll')
    : (error || t('orderList.sheinEmptyDesc'));

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, index)).filter(Boolean),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key, row) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        if (row) selectedRowsRef.current.set(key, row);
      }
      return next;
    });
  };

  useEffect(() => {
    rows.forEach((row, index) => {
      const key = rowKey(row, index);
      if (selectedKeys.has(key)) selectedRowsRef.current.set(key, row);
    });
  }, [rows, selectedKeys]);

  const selectedRows = useMemo(() => {
    const map = new Map(selectedRowsRef.current);
    selectableKeys.forEach((key) => {
      if (!selectedKeys.has(key)) map.delete(key);
    });
    rows.forEach((row, index) => {
      const key = rowKey(row, index);
      if (selectedKeys.has(key)) map.set(key, row);
    });
    return Array.from(selectedKeys)
      .map((key) => map.get(key))
      .filter(Boolean);
  }, [rows, selectedKeys, selectableKeys]);

  const openDetail = (orderNo) => {
    if (!orderNo) return;
    navigate(buildSheinPath(`orders/${encodeURIComponent(orderNo)}`));
  };

  const openTrack = (row, packageIdx = 0) => {
    const key = rowKey(row, 0);
    const logistics = listLogisticsSummary(row);
    const withWaybill = logistics.items.filter((i) => i.waybillNo);
    if (!withWaybill.length) {
      showToast(t('orderList.noWaybill'), 'warning');
      return;
    }
    const idx = Math.min(Math.max(0, packageIdx), withWaybill.length - 1);
    setTrackPanel({
      key,
      packageIdx: idx,
      items: withWaybill,
      liveLoading: false,
      liveError: '',
      liveTrack: null,
    });
  };

  const refreshLiveTrack = async (row) => {
    if (!activeShopId || !trackPanel) return;
    const item = trackPanel.items[trackPanel.packageIdx];
    const orderNo = String(pickField(row, 'orderNo', 'orderNumber') || '').trim();
    if (!orderNo || !item) return;
    if (!item.waybillNo) {
      showToast(t('orderList.missingWaybill'), 'warning');
      return;
    }
    setTrackPanel((prev) => (prev ? { ...prev, liveLoading: true, liveError: '', liveTrack: null } : prev));
    try {
      const data = await sheinPlatformAPI.getSheinOrderLogisticsTrack(activeShopId, {
        orderNo,
        waybillNo: item.waybillNo,
      });
      setTrackPanel((prev) => (prev ? { ...prev, liveLoading: false, liveTrack: data } : prev));
    } catch (e) {
      const raw = String(e?.message || '');
      const missing = /9999002|数据不存在/i.test(raw);
      const message = missing ? t('orderList.noTrackData') : t('orderList.noTrack');
      setTrackPanel((prev) => (prev ? { ...prev, liveLoading: false, liveError: message } : prev));
      if (!missing) showToast(message, 'warning');
    }
  };

  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('orderList.lastUpdate', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : '';

  const exportPage = () => {
    if (!rows.length) {
      showToast(t('orderList.noneToExport'), 'warning');
      return;
    }
    const headers = [t('orderList.orderNo'), t('orderList.status'), t('orderList.site'), t('orderList.ordered'), t('orderList.paid'), t('orderList.signed'), t('orderList.income'), t('orderList.product'), t('orderList.waybillNo')];
    const csvRows = rows.map((row) => {
      const product = listOrderProductSummary(row);
      const dates = listDateRows(row);
      const prices = listPrimaryPrices(row);
      const logistics = listLogisticsSummary(row);
      return [
        pickField(row, 'orderNo', 'orderNumber') || '',
        statusText(pickField(row, 'orderStatus')),
        pickField(row, 'salesSite', 'site') || '',
        dates[0]?.value || '',
        dates[1]?.value || '',
        dates[2]?.value || '',
        prices.primary[0]?.value || '',
        product.title || '',
        logistics.items.map((i) => i.waybillNo || i.packageNo).filter(Boolean).join('|'),
      ];
    });
    downloadCsv(`shein-orders-${Date.now()}.csv`, headers, csvRows);
    showToast(t('orderList.exported', { count: csvRows.length }), 'success');
  };

  const exportFiltered = async () => {
    if (!activeShopId) {
      showToast(t('orderList.pickShop'), 'warning');
      return;
    }
    if (total <= 0 && rows.length === 0) {
      showToast(t('orderList.noneToExport'), 'warning');
      return;
    }
    setExporting(true);
    try {
      const { _nonce, ...baseParams } = queryParams;
      const all = await fetchAllPages(async (pageNo, size) => {
        const res = await sheinPlatformAPI.listSheinOrders(activeShopId, {
          ...baseParams,
          page: pageNo,
          pageSize: size,
        });
        const list = extractSheinList(res);
        return { items: list, total: extractSheinTotal(res, list.length) };
      });
      const headers = [t('orderList.orderNo'), t('orderList.status'), t('orderList.site'), t('orderList.ordered'), t('orderList.paid'), t('orderList.signed'), t('orderList.income'), t('orderList.product')];
      const csvRows = all.map((row) => {
        const product = listOrderProductSummary(row);
        const dates = listDateRows(row);
        const prices = listPrimaryPrices(row);
        return [
          pickField(row, 'orderNo', 'orderNumber') || '',
          statusText(pickField(row, 'orderStatus')),
          pickField(row, 'salesSite', 'site') || '',
          dates[0]?.value || '',
          dates[1]?.value || '',
          dates[2]?.value || '',
          prices.primary[0]?.value || '',
          product.title || '',
        ];
      });
      if (!csvRows.length) {
        showToast(t('orderList.noneToExport'), 'warning');
        return;
      }
      downloadCsv(`shein-orders-filtered-${Date.now()}.csv`, headers, csvRows);
      showToast(t('orderList.exported', { count: csvRows.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('orderList.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const exportSelected = () => {
    if (!selectedRows.length) {
      showToast(t('orderList.pickToExport'), 'warning');
      return;
    }
    const headers = [t('orderList.orderNo'), t('orderList.status'), t('orderList.site'), t('orderList.ordered'), t('orderList.paid'), t('orderList.signed'), t('orderList.income'), t('orderList.product'), t('orderList.waybillNo')];
    const csvRows = selectedRows.map((row) => {
      const product = listOrderProductSummary(row);
      const dates = listDateRows(row);
      const prices = listPrimaryPrices(row);
      const logistics = listLogisticsSummary(row);
      return [
        pickField(row, 'orderNo', 'orderNumber') || '',
        statusText(pickField(row, 'orderStatus')),
        pickField(row, 'salesSite', 'site') || '',
        dates[0]?.value || '',
        dates[1]?.value || '',
        dates[2]?.value || '',
        prices.primary[0]?.value || product.incomeText || '',
        product.title || '',
        logistics.items.map((i) => i.waybillNo || i.packageNo).filter(Boolean).join('|'),
      ];
    });
    downloadCsv(`shein-orders-selected-${Date.now()}.csv`, headers, csvRows);
    showToast(t('orderList.exported', { count: csvRows.length }), 'success');
  };

  const onBatchDelete = async () => {
    if (!activeShopId || selectedKeys.size === 0) {
      showToast(t('orderList.pickToDelete'), 'warning');
      return;
    }
    const orderNos = selectedRows
      .map((row) => pickField(row, 'orderNo', 'orderNumber'))
      .filter((no) => no != null && String(no).trim() !== '')
      .map((no) => String(no).trim());
    const ids = selectedRows
      .map((row) => Number(row?.id ?? pickField(row, 'id')))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (orderNos.length === 0 && ids.length === 0) {
      showToast(t('orderList.missingOrderNo'), 'error');
      return;
    }
    const countHint = orderNos.length || ids.length;
    const ok = await showSheinConfirm({
      title: t('orderList.batchDelete'),
      message: t('orderList.batchDeleteMsg', { count: countHint, platform: 'SHEIN' }),
      tone: 'danger',
      confirmText: t('orderList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await sheinPlatformAPI.batchDeleteSheinOrders(activeShopId, {
        orderNos,
        ids,
      });
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : countHint;
      if (count > 0) {
        showToast(t('orderList.deletedCount', { count }), 'success');
      } else {
        showToast(t('orderList.noneDeleted'), 'warning');
      }
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      load();
    } catch (e) {
      showToast(e?.message || t('orderList.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const orderIdLabel = t(ORDER_ID_OPTIONS.find((o) => o.value === filterDraft.orderIdType)?.labelKey || 'orderList.orderNo');

  return (
    <>
      <SheinSyncProgressSlot kinds={['orders']} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || exporting || deleting}
            syncKinds={['orders']}
            onSync={() => {
              if (!listReady) {
                showToast(t('orderList.pickAuthorized', { platform: 'SHEIN' }), 'warning');
                return;
              }
              if (isAllShopsScope(pageShopScope) || isAllShopsMode) {
                setShopPickOpen(true);
                return;
              }
              setSyncRangeOpen(true);
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={shopLoading || !listReady || syncing}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('orderList.syncHint', { platform: 'SHEIN' })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={exporting || !listReady}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!activeShopId || deleting}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline spo-field--id-combo spo-field--batch spo-field--w300">
              <select
                className="spo-select spo-select--compact spo-select--order-type"
                value={filterDraft.orderIdType}
                disabled={!activeShopId}
                aria-label={t('orderList.idTypeAria')}
                onChange={(e) => updateField('orderIdType', e.target.value)}
              >
                {ORDER_ID_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </select>
              <input
                className="spo-input"
                value={filterDraft.orderIdValue}
                placeholder={t('orderList.idPlaceholder', { type: orderIdLabel })}
                disabled={!activeShopId}
                onChange={(e) => updateField('orderIdValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={t('orderList.batchInputId', { type: orderIdLabel })}
                title={t('orderList.batchQuery')}
                disabled={!activeShopId}
                onClick={() => {
                  setBatchModalField('orderIdValue');
                  setBatchModalOpen(true);
                }}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="spo-field spo-field--inline spo-field--id-combo spo-field--batch spo-field--w300">
              <span className="spo-field__label">SKC</span>
              <input
                className="spo-input"
                value={filterDraft.skc}
                placeholder={t('orderList.skcPlaceholder')}
                disabled={!activeShopId}
                onChange={(e) => updateField('skc', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={t('orderList.batchInputSkc')}
                title={t('orderList.batchQuery')}
                disabled={!activeShopId}
                onClick={() => {
                  setBatchModalField('skc');
                  setBatchModalOpen(true);
                }}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <SheinDateRangeFilter
              showTimeType={false}
              startDate={filterDraft.startTime}
              endDate={filterDraft.endTime}
              disabled={!activeShopId}
              onStartDateChange={(v) => updateField('startTime', pickDatePart(v))}
              onEndDateChange={(v) => updateField('endTime', pickDatePart(v))}
            />
          </div>
          <div className="spo-filter-bar__actions">
            <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onReset} disabled={!activeShopId}>
              {t('orderList.reset')}
            </button>
            <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onQuery} disabled={!activeShopId || loading}>
              {t('orderList.query')}
            </button>
          </div>
        </div>
        {error ? <div className="spo-ops-error" role="alert">{error}</div> : null}
      </ProductFilterCard>

      <BatchQueryModal
        open={batchModalOpen}
        title={batchModalField === 'skc' ? t('orderList.batchInputSkc') : t('orderList.batchInputId', { type: orderIdLabel })}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalField === 'skc'
          ? t('orderList.skcLinePh')
          : t('orderList.idLinePh', { type: orderIdLabel })}
        onConfirm={(tokens) => {
          if (batchModalField) {
            updateField(batchModalField, joinBatchTokens(tokens));
          }
          setBatchModalOpen(false);
          setBatchModalField(null);
        }}
        onClose={() => {
          setBatchModalOpen(false);
          setBatchModalField(null);
        }}
      />

      <SheinSyncRangeModal
        open={syncRangeOpen}
        title={t('orderList.syncOrders')}
        defaultHours={24 * 7}
        presets={[
          { label: t('orderList.last24h'), hours: 24 },
          { label: t('orderList.last7d'), hours: 24 * 7 },
          { label: t('orderList.last14d'), hours: 24 * 14 },
          { label: t('orderList.last30d'), hours: 24 * 30 },
          { label: t('orderList.last90d'), hours: 24 * 90 },
        ]}
        confirming={syncing}
        onConfirm={async (range) => {
          pendingSyncRangeRef.current = range;
          const ok = await guardedSync();
          if (ok !== false) setSyncRangeOpen(false);
        }}
        onClose={() => {
          if (!syncing) setSyncRangeOpen(false);
        }}
        onClose={() => setSyncRangeOpen(false)}
      />

      <ProductListCard
        loading={Boolean(activeShopId) && loading}
        loadingLabel={t('orderList.querying')}
        tabs={listTabs}
      >
        <div className="spo-drafts__table-wrap spo-ops-table-wrap">
          <table className="spo-publish__table spo-drafts__table spo-ops-table spo-ops-table--order">
            <colgroup>
              <col className="spo-ops-col spo-ops-col--check shein-product-col--check" />
              <col className="spo-ops-col spo-ops-col--product" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--logistics" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--date" />
              <col className="spo-ops-col spo-ops-col--price" />
              <col className="spo-ops-col spo-ops-col--ops" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="spo-ops-th spo-ops-th--check shein-product-cell--spu-check"
                  onClick={() => {
                    if (!activeShopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('orderList.selectAll')}
                    disabled={!activeShopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col">{t('orderList.productInfo')}</th>
                <th scope="col">{t('orderList.orderInfo')}</th>
                <th scope="col">{t('orderList.logisticsInfo')}</th>
                <th scope="col">{t('orderList.status')}</th>
                <th scope="col">{t('orderList.date')}</th>
                <th scope="col">{t('orderList.priceInfo')}</th>
                <th scope="col" className="spo-ops-th--ops">{t('orderList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!activeShopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="spo-certs-empty-cell">
                    <div className="spo-certs-empty">
                      <p className="spo-certs-empty__title">{emptyTitle}</p>
                      {emptyDesc ? <p className="spo-certs-empty__desc">{emptyDesc}</p> : null}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.flatMap((row, index) => {
                  const key = rowKey(row, index);
                  const no = pickField(row, 'orderNo', 'orderNumber');
                  const statusCode = pickField(row, 'orderStatus');
                  const product = listOrderProductSummary(row);
                  const orderInfo = listOrderInfoRows(row);
                  const logistics = listLogisticsSummary(row);
                  const dates = listDateRows(row);
                  const prices = listPrimaryPrices(row);
                  const trackOpen = trackPanel?.key === key;

                  const main = (
                    <tr key={key}>
                      <td
                        className="spo-ops-td spo-ops-td--check shein-product-cell--spu-check"
                        onClick={() => toggleOne(key, row)}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('orderList.selectRow', { title: no || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key, row)}
                        />
                      </td>
                      <td className="spo-ops-td spo-ops-td--product">
                        {product.title || product.imageUrl || product.skc || product.merchantSku || product.supplierCode || product.incomeText ? (
                          <div className="spo-return-product">
                            {product.imageUrl ? (
                              <SheinProductThumb
                                imageUrl={product.imageUrl}
                                className="spo-return-product__img"
                                placeholderClassName="spo-return-product__img spo-return-product__img--empty"
                                alt={product.title || t('orderList.productAlt')}
                              />
                            ) : (
                              <div className="spo-return-product__img spo-return-product__img--empty" />
                            )}
                            <div className="spo-return-product__meta">
                              <HoverCopyField
                                value={product.title || ''}
                                className="spo-return-product__title"
                                popoverCopy
                              />
                              <HoverCopyField
                                label="SKC"
                                value={product.skc || ''}
                                className="spo-return-product__sku"
                              />
                              <HoverCopyField
                                label={t('orderList.merchantSku')}
                                value={product.merchantSku || ''}
                                className="spo-return-product__sku"
                              />
                              <HoverCopyField
                                label={t('orderList.supplierCode')}
                                value={product.supplierCode || ''}
                                className="spo-return-product__sku"
                              />
                              <div className="spo-return-product__refund">
                                {t('orderList.estIncomePrefix')}{product.incomeText || '—'}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="spo-return-product__empty">{t('orderList.noProduct')}</span>
                        )}
                      </td>
                      <td className="spo-ops-td spo-ops-td--orderno">
                        <HoverCopyField
                          className="spo-return-copy"
                          value={no}
                          display={no ? <strong>{no}</strong> : '—'}
                        />
                        <div className="spo-order-meta-list">
                          {orderInfo.map((item) => (
                            <div key={item.label}>
                              <span className="spo-return-times__label">{item.label}</span>
                              <span>{item.value || '—'}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="spo-ops-td spo-ops-td--logistics">
                        {logistics.items.length === 0 ? (
                          <span className="spo-return-product__empty">{t('orderList.noLogistics')}</span>
                        ) : (
                          <div className="spo-order-logistics">
                            {logistics.items.slice(0, 2).map((item, i) => (
                              <div key={`${item.packageNo}-${item.waybillNo}-${i}`} className="spo-order-logistics__item">
                                <div>{t('orderList.carrierPrefix')}{item.carrier || '—'}</div>
                                <HoverCopyField
                                  label={t('orderList.packageLabel')}
                                  value={item.packageNo || ''}
                                  className="spo-return-copy"
                                />
                                <HoverCopyField
                                  label={t('orderList.waybillLabel')}
                                  value={item.waybillNo || ''}
                                  className="spo-return-copy"
                                />
                                {item.latestNode ? <div className="spo-order-logistics__node">{item.latestNode}</div> : null}
                              </div>
                            ))}
                            {logistics.items.length > 2 ? (
                              <div className="spo-order-logistics__more">{t('orderList.morePackages', { count: logistics.items.length })}</div>
                            ) : null}
                            <button
                              type="button"
                              className="spo-btn spo-btn--ghost spo-btn--sm"
                              disabled={!logistics.items.some((i) => i.waybillNo)}
                              title={logistics.items.some((i) => i.waybillNo) ? t('orderList.viewOrderTrack') : t('orderList.noWaybillShort')}
                              onClick={() => (trackOpen ? setTrackPanel(null) : openTrack(row, 0))}
                            >
                              {trackOpen ? t('orderList.hideTrack') : t('orderList.viewTrack')}
                            </button>
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`spo-records__status spo-records__status--${statusModifier(statusCode)}`}>
                          {statusText(statusCode) || '—'}
                        </span>
                      </td>
                      <td className="spo-ops-td spo-ops-td--times">
                        <div className="spo-return-times">
                          {dates.map((d) => (
                            <div key={d.label}>
                              <span className="spo-return-times__label">{d.label}</span>
                              <span>{d.value || '—'}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="spo-ops-td spo-ops-td--price">
                        <div className="spo-return-times">
                          {(prices.primary.length ? prices.primary : [{ label: t('orderList.fee'), value: '—' }]).map((p) => (
                            <div key={p.label}>
                              <span className="spo-return-times__label">{p.label}</span>
                              <span>{p.value || '—'}</span>
                            </div>
                          ))}
                          {prices.extra.length ? (
                            <details className="spo-order-price-more">
                              <summary>{t('orderList.moreFees')}</summary>
                              {prices.extra.map((p) => (
                                <div key={p.label}>
                                  <span className="spo-return-times__label">{p.label}</span>
                                  <span>{p.value}</span>
                                </div>
                              ))}
                            </details>
                          ) : null}
                        </div>
                      </td>
                      <td className="spo-ops-td spo-ops-td--ops spo-certs-td spo-certs-td--ops">
                        <div className="spo-drafts__actions">
                          <button
                            type="button"
                            className="spo-btn spo-btn--primary spo-btn--sm"
                            disabled={!no}
                            onClick={() => openDetail(no)}
                          >
                            {t('orderList.detail')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );

                  if (!trackOpen) return [main];

                  const item = trackPanel.items[trackPanel.packageIdx] || trackPanel.items[0];
                  const snap = trackPanel.liveTrack
                    ? { track: trackPanel.liveTrack, packageNo: item?.packageNo, waybillNo: item?.waybillNo }
                    : item?.trackSnap;
                  const tracks = trackInfoList(snap?.track || snap);

                  return [
                    main,
                    <tr key={`${key}-track`} className="spo-table__detail-row">
                      <td colSpan={8}>
                        <div className="spo-order-track">
                          <div className="spo-order-track__toolbar">
                            <strong>{t('orderList.orderTrack')}</strong>
                            {trackPanel.items.length > 1 ? (
                              <label className="spo-field spo-field--inline">
                                <span className="spo-field__label">{t('orderList.packageLabel')}</span>
                                <select
                                  className="spo-select"
                                  value={String(trackPanel.packageIdx ?? 0)}
                                  onChange={(e) => openTrack(row, Number(e.target.value))}
                                >
                                  {trackPanel.items.map((ref, i) => (
                                    <option key={`${ref.packageNo}-${ref.waybillNo}-${i}`} value={i}>
                                      {ref.packageNo || ref.waybillNo || t('orderList.packageN', { index: i + 1 })}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            ) : null}
                            <button
                              type="button"
                              className="spo-btn spo-btn--secondary spo-btn--sm"
                              disabled={trackPanel.liveLoading}
                              onClick={() => refreshLiveTrack(row)}
                            >
                              {trackPanel.liveLoading ? t('orderList.refreshingTrack') : t('orderList.refreshTrack')}
                            </button>
                          </div>
                          {trackPanel.liveError ? (
                            <div className="spo-order-track__empty">{trackPanel.liveError}</div>
                          ) : trackPanel.liveLoading ? (
                            <div className="spo-order-track__empty">{t('orderList.loadingTrack')}</div>
                          ) : tracks.length === 0 ? (
                            <div className="spo-order-track__empty">
                              {/9999002|数据不存在/i.test(String(item?.error || ''))
                                ? t('orderList.noTrackData')
                                : (item?.error
                                  ? t('orderList.noTrack')
                                  : t('orderList.noTrackNode', { platform: 'SHEIN' }))}
                            </div>
                          ) : (
                            tracks.map((info, infoIdx) => (
                              <div key={`${pickField(info, 'waybillNo') || infoIdx}`} className="spo-order-track__block">
                                <div className="spo-order-track__meta">
                                  <span>{t('orderList.waybillPrefix')}{pickField(info, 'waybillNo') || item?.waybillNo || '—'}</span>
                                  <span>{t('orderList.carrierPrefix')}{pickField(info, 'carrier') || item?.carrier || '—'}</span>
                                  <span>{t('orderList.typePrefix')}{pickField(info, 'waybillType') || '—'}</span>
                                </div>
                                <ol className="spo-order-track__timeline">
                                  {(Array.isArray(info.tracking) ? info.tracking : []).map((node, nodeIdx) => (
                                    <li key={`${nodeIdx}-${pickField(node, 'updateTimeMillis')}`}>
                                      <div className="spo-order-track__time">
                                        {formatTrackTime(pickField(node, 'updateTimeMillis'))}
                                      </div>
                                      <div className="spo-order-track__node">
                                        <strong>{pickField(node, 'nodeCodeName', 'nodeCode') || t('orderList.node')}</strong>
                                        <span>{pickField(node, 'description') || '—'}</span>
                                      </div>
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            ))
                          )}
                        </div>
                      </td>
                    </tr>,
                  ];
                })
              )}
            </tbody>
          </table>
        </div>

        <SheinOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!listReady}
          pageItemCount={rows.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('orderList.pager')}
        />
      </ProductListCard>
      <PlatformShopPickModal
        open={shopPickOpen}
        shops={scopedShops}
        title={fixedUi("选择要同步订单的店铺")}
        description="全部视图下请选择一家店铺后，再调用 SHEIN 平台接口同步订单。"
        confirmText="继续同步"
        initialShopId={concreteScopeId || ''}
        onClose={() => setShopPickOpen(false)}
        onConfirm={(shopId) => {
          setShopPickOpen(false);
          setPageShopScope(String(shopId));
          setTimeout(() => setSyncRangeOpen(true), 0);
        }}
      />
    </>
  );
}
